import { type Rate_userProfile$key } from '#app/__generated__/relay/Rate_userProfile.graphql.js';
import { getRateRangeFromMinHourlyRate } from '#app/components/DurationAndRate/utils.js';
import { InlineEditContainer } from '#app/components/InlineEditContainer/InlineEditContainer.js';
import { Text } from '#app/components/Primitives/Text.js';
import { StyledTagBox, Tag } from '#app/components/Tags/Tag.js';
import { TagListMenu } from '#app/components/Tags/TagListMenu/TagListMenu.js';
import { Tooltip } from '#app/components/Tooltip/Tooltip.js';
import { EditIcon, EyeOffIcon } from '@contra/icons/icons';
import { graphql, useFragment } from '@contra/relay';
import { lazy, Suspense, useId, useState } from 'react';
import { styled } from 'styled-components';

type RateTags = {
  id: string;
  logoImage?: null | { uid: string };
  name: string;
};

const LazyRateInlineEditing = lazy(async () => {
  return {
    default: (await import('./RateInlineEditing.js')).RateInlineEditing,
  };
});

const itemHeight = 32;

const TagListMenuContainer = styled.div`
  ${StyledTagBox} {
    height: ${itemHeight}px;
    border-radius: 20px;

    ${Text} {
      font-size: 14px;
    }
  }
`;

export const Rate = ({
  userProfileRef,
}: {
  readonly userProfileRef: Rate_userProfile$key;
}) => {
  const data = useFragment(
    graphql`
      fragment Rate_userProfile on UserProfile {
        workPreferences {
          minimumHourlyRate
        }
        visitorCanEdit
      }
    `,
    userProfileRef,
  );

  const { visitorCanEdit, workPreferences } = data;

  const [isInlineEditing, setIsInlineEditing] = useState(false);

  const rateItemId = useId();

  const rate = workPreferences.minimumHourlyRate
    ? getRateRangeFromMinHourlyRate(workPreferences.minimumHourlyRate)
    : null;

  const rateItem = rate ? { id: rateItemId, name: rate } : null;

  const visitorCanEditDefaultState = rateItem ? (
    <InlineEditContainer
      label="Edit your rate"
      onClick={() => setIsInlineEditing(true)}
      visitorCanEdit={visitorCanEdit}
    >
      <TagListMenuContainer>
        <TagListMenu<RateTags>
          itemHeight={itemHeight}
          items={[rateItem]}
          maxRows={2}
          readOnly
          transformItemToTag={(item) => {
            return {
              label: item.name,
              value: item.id,
              variant: 'blue',
            };
          }}
        />
      </TagListMenuContainer>
      <EditIcon size={16} />
    </InlineEditContainer>
  ) : (
    <InlineEditContainer
      label="Edit your rate"
      onClick={() => {
        setIsInlineEditing(true);
      }}
      visitorCanEdit={visitorCanEdit}
    >
      <Tooltip
        content="This is only visible to you. Omitting a rate will impact your search ranking and job matching experience."
        placement="bottom-start"
      >
        <Tag
          icon={<EyeOffIcon size={16} />}
          iconSize={16}
          label="I'd prefer not to say"
          variant="gray20"
        />
      </Tooltip>
      <EditIcon size={16} />
    </InlineEditContainer>
  );

  if (visitorCanEdit) {
    if (isInlineEditing) {
      return (
        <Suspense fallback={visitorCanEditDefaultState}>
          <LazyRateInlineEditing
            fetchedMinimumHourlyRate={workPreferences.minimumHourlyRate}
            setIsInlineEditing={setIsInlineEditing}
          />
        </Suspense>
      );
    }

    return visitorCanEditDefaultState;
  }

  if (rateItem) {
    return (
      <TagListMenu<RateTags>
        itemHeight={itemHeight}
        items={[rateItem]}
        maxRows={10}
        readOnly
        transformItemToTag={(item) => {
          return {
            label: item.name,
            value: item.id,
            variant: 'blue',
          };
        }}
      />
    );
  }

  return null;
};
